// `useConversations` — the LEAN end-user data hook (fn-48).
//
// NOT the operator fan-out: `useInboxData`'s 8-way per-status
// `listSessions` calls serve inbox grouping; an end user needs ONE
// list. The CP is user-scoped post-fn-46, so a single unfiltered
// `listSessions({ limit: 50 })` IS "my conversations" by construction.
// `listApprovals` rides along for needs-attention correlation (the
// pending approvals per session feed the in-chat action cards).
//
// Polling is SERIALIZED — same fixed pattern as `use-inbox-data.ts`
// post-CodeRabbit: the next tick is scheduled (setTimeout) only after
// the current tick's fetches have ALL settled, so a response slower
// than the interval still lands instead of being starved (memory:
// tick-wide epochs starve under slow responses — the epoch guards the
// EFFECT lifetime, never individual ticks).
//
// Client-side derivation: archived rows filtered out FIRST (memory:
// scope-filters-must-run-before-mergecap — filter before any cap; the
// 50-row cap here is server-side via `limit`, and `truncated`
// describes the fetched set via `nextCursor`), sorted by
// `last_updated_at` desc, needs-attention = status ∈
// {awaiting_user, awaiting_approval} — the ONLY attention signal
// (read-state tracking is a non-goal).
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePlatformAdapter } from '../context.js';
/** Default poll cadence (ms) — consumer-tunable via `refreshIntervalMs`. */
export const DEFAULT_CONVERSATIONS_REFRESH_MS = 5_000;
/** Single-list page size — the v1 cap (cursor load-more is a follow-up). */
export const CONVERSATIONS_LIMIT = 50;
/** Page limit for the approvals correlation fetch (first page only). */
export const CONVERSATIONS_APPROVALS_LIMIT = 50;
/** Statuses that pin a conversation into "Needs your attention". */
export const NEEDS_ATTENTION_STATUSES = [
    'awaiting_user',
    'awaiting_approval',
];
function errorMessage(err) {
    if (err instanceof Error && err.message.length > 0)
        return err.message;
    return String(err);
}
function isTruncated(nextCursor) {
    return typeof nextCursor === 'string' && nextCursor.length > 0;
}
function isArchived(session) {
    return session.metadata?.archived === true;
}
function lastUpdatedMs(session) {
    const t = new Date(session.last_updated_at).getTime();
    return Number.isFinite(t) ? t : 0;
}
/**
 * Pure derivation: archived filtered FIRST, then sorted desc by last
 * activity, approvals correlated by session id, needs-attention
 * flagged + subset extracted. Exported for direct unit testing.
 */
export function deriveConversations(sessions, approvals) {
    const approvalsBySession = new Map();
    for (const approval of approvals) {
        const list = approvalsBySession.get(approval.session_id);
        if (list === undefined) {
            approvalsBySession.set(approval.session_id, [approval]);
        }
        else {
            list.push(approval);
        }
    }
    const conversations = sessions
        .filter((s) => !isArchived(s))
        .sort((a, b) => lastUpdatedMs(b) - lastUpdatedMs(a))
        .map((session) => ({
        session,
        needsAttention: NEEDS_ATTENTION_STATUSES.includes(session.status),
        pendingApprovals: approvalsBySession.get(session.id) ?? [],
    }));
    return {
        conversations,
        needsAttention: conversations.filter((c) => c.needsAttention),
    };
}
export function useConversations(options = {}) {
    const adapter = usePlatformAdapter();
    const refreshIntervalMs = options.refreshIntervalMs ?? DEFAULT_CONVERSATIONS_REFRESH_MS;
    const [sessions, setSessions] = useState({ items: [], loading: true, error: null, truncated: false });
    const [approvals, setApprovals] = useState({ items: [], error: null });
    const [refreshNonce, setRefreshNonce] = useState(0);
    // Effect epoch — guards across effect LIFETIMES (unmount, adapter
    // change, manual refresh re-run), NOT across ticks: ticks are
    // serialized within one effect, so within-lifetime responses are
    // always current and must never be dropped.
    const epochRef = useRef(0);
    // Adapter identity transition — the previous adapter's conversations
    // and approvals must NEVER stay visible under a new adapter (a
    // provider/account swap would briefly expose another user's data,
    // indefinitely if the new adapter errors). Reset to the initial
    // loading state synchronously on swap; stale-on-error is preserved
    // only WITHIN one adapter's lifetime. Same rule as useAgentNames.
    const adapterRef = useRef(adapter);
    if (adapterRef.current !== adapter) {
        adapterRef.current = adapter;
        setSessions({ items: [], loading: true, error: null, truncated: false });
        setApprovals({ items: [], error: null });
    }
    const refresh = useCallback(() => {
        setRefreshNonce((n) => n + 1);
    }, []);
    useEffect(() => {
        // `refreshNonce` is the manual-refresh trigger: `refresh()` bumps
        // it to re-run this effect (immediate tick + schedule reset).
        void refreshNonce;
        epochRef.current += 1;
        const epoch = epochRef.current;
        const live = () => epochRef.current === epoch;
        let timer = null;
        // One tick = ONE unfiltered list + the approvals correlation;
        // resolves when both settled (each branch absorbs its own
        // rejection into state — stale items are kept, never blanked).
        const runTick = (adapterRef) => {
            const jobs = [
                Promise.resolve()
                    .then(() => adapterRef.listSessions({ limit: CONVERSATIONS_LIMIT }))
                    .then((page) => {
                    if (!live())
                        return;
                    setSessions({
                        items: page.data,
                        loading: false,
                        error: null,
                        truncated: isTruncated(page.nextCursor),
                    });
                })
                    .catch((err) => {
                    if (!live())
                        return;
                    setSessions((prev) => ({
                        items: prev.items,
                        loading: false,
                        error: errorMessage(err),
                        truncated: prev.truncated,
                    }));
                }),
                Promise.resolve()
                    .then(() => adapterRef.listApprovals({ limit: CONVERSATIONS_APPROVALS_LIMIT }))
                    .then((page) => {
                    if (!live())
                        return;
                    setApprovals({ items: page.data, error: null });
                })
                    .catch((err) => {
                    if (!live())
                        return;
                    setApprovals((prev) => ({ items: prev.items, error: errorMessage(err) }));
                }),
            ];
            return Promise.allSettled(jobs).then(() => undefined);
        };
        const loop = () => {
            void runTick(adapter).then(() => {
                if (!live())
                    return;
                // Schedule the NEXT tick only after this one fully settled —
                // serialized polling, one outstanding tick at a time.
                if (refreshIntervalMs > 0) {
                    timer = setTimeout(loop, refreshIntervalMs);
                }
            });
        };
        loop();
        return () => {
            // Invalidate in-flight responses + stop the schedule.
            epochRef.current += 1;
            if (timer !== null)
                clearTimeout(timer);
        };
    }, [adapter, refreshIntervalMs, refreshNonce]);
    const derived = useMemo(() => deriveConversations(sessions.items, approvals.items), [sessions.items, approvals.items]);
    return {
        conversations: derived.conversations,
        needsAttention: derived.needsAttention,
        loading: sessions.loading,
        error: sessions.error ?? approvals.error,
        truncated: sessions.truncated,
        refresh,
    };
}
//# sourceMappingURL=use-conversations.js.map